import React, { useState, useEffect } from 'react';
import Dropdown from '../../../../common/Dropdown/Dropdown';
import FormatterBar from '../../../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar';
import styles from './AddGoalModal.module.scss';
import { useTranslation } from 'react-i18next';
import Button from '../../../../common/Button/Button';
import { useAppSelector } from '../../../../../redux/hooks';
import ErrorMessage from '../../../../../utils/fieldErrorMesssage';
import DropdownMultiselect from '../../../../common/DropdownMultiselect/DropdownMultiselect';
import {
  INCOME_TYPES,
  PERFORMANCE_METRICS,
  TIME_FRAME_TYPE,
} from '../../../../../constants/dataConstants';
import { CurrencyInputFormatter } from '../../../../../utils/currencyFormatter';

type GoalDropdownItem = {
  code: string | number;
  value: string;
};

export const OUTCOMES: GoalDropdownItem[] = [
  { code: '1', value: 'Qualification' },
  { code: '2', value: 'Income' },
  { code: '9', value: 'Other' },
];

const AWARD_TYPES: GoalDropdownItem[] = [
  { code: '342', value: 'Founders Platinum' },
  { code: '345', value: 'Founders Ruby' },
  { code: '350', value: 'Pearl' },
  { code: '365', value: 'Sapphire' },
  { code: '367', value: 'Founders Sapphire' },
  { code: '370', value: 'Emerald' },
  { code: '375', value: 'Founders Emerald' },
  { code: '380', value: 'Diamond' },
  { code: '385', value: 'Founders Diamond' },
  { code: '390', value: 'Executive Diamond' },
  { code: '395', value: 'Founders Executive Diamond' },
  { code: '400', value: 'Double Diamond' },
  { code: '405', value: 'Founders Double Diamond' },
  { code: '410', value: 'Triple Diamond' },
  { code: '415', value: 'Founders Triple Diamond' },
  { code: '420', value: 'Crown' },
  { code: '425', value: 'Founders Crown' },
  { code: '430', value: 'Crown Ambassador' },
  { code: '435', value: 'Founders Crown Ambassador' },
];

interface GoalProps {
  initialGoal?: GoalData | HistoryItem;
  onClose: () => void;
  onSave: (goalData: any) => void;
}
interface GoalErrors {
  goalTitle?: string;
  kpiCodes?: string;
  outcome?: string;
  incomeType?: string;
  monthlyIncome?: string;
  awardType?: string;
  [key: string]: string | undefined;
}

const AddGoalModal: React.FC<GoalProps> = ({ initialGoal, onClose, onSave }) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [timeFrame, setTimeFrame] = useState<GoalDropdownItem | undefined>();
  const [textData, setTextData] = useState<string>('');
  const [kpiCodes, setKpiCodes] = useState<any[]>([]);

  const [outcome, setOutcome] = useState<GoalDropdownItem | undefined>();
  const [incomeType, setIncomeType] = useState<GoalDropdownItem | undefined>();
  const [monthlyIncome, setMonthlyIncome] = useState<string | null>(null);
  const [awardType, setAwardType] = useState<GoalDropdownItem | undefined>();
  const [goalTitle, setGoalTitle] = useState<string>('');
  const [isIncomeFocused, setIsIncomeFocused] = useState(false);
  const [isGoalTitleFocused, setIsGoalTitleFocused] = useState(false);
  const outcomeCode = outcome?.code;
  const [errors, setErrors] = useState<{ [key: string]: string }>();
  const currencyCode = useAppSelector((state: any) => state.currency.currencyCode);

  useEffect(() => {
    if (initialGoal) {
      setGoalTitle(initialGoal.title || '');
      setTimeFrame(
        TIME_FRAME_TYPE.find(
          (time) => time?.code?.toString() === initialGoal?.timeframeType?.toString()
        ) || undefined
      );
      setKpiCodes(
        initialGoal.kpiCodes
          ? initialGoal.kpiCodes
              .map((val: string) =>
                Object.values(PERFORMANCE_METRICS)
                  .flat()
                  .find((metrics) => metrics.code === val)
              )
              .filter(Boolean)
          : []
      );
      setOutcome(
        OUTCOMES.find((o) => o?.code?.toString() === initialGoal?.outcomeType?.toString()) ||
          undefined
      );

      setIncomeType(
        INCOME_TYPES.find(
          (i) => i?.code?.toString() === initialGoal?.outcomeDetail?.incomeType?.toString()
        ) || undefined
      );

      setMonthlyIncome(initialGoal?.outcomeDetail?.incomeValue.toString() || null);
      setAwardType(
        AWARD_TYPES.find(
          (a) => a?.code?.toString() === initialGoal?.outcomeDetail?.awardCode?.toString()
        ) || undefined
      );
      setTextData(initialGoal?.note || '');
    }
  }, [initialGoal]);

  const handleOutcomeChange = (item: GoalDropdownItem) => {
    setOutcome(item);
    setIncomeType(undefined);
    setMonthlyIncome('');
    setAwardType(undefined);

    setErrors((prev) => ({
      ...prev,
      outcome: '',
      incomeType: '',
      monthlyIncome: '',
      awardType: '',
    }));
  };

  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incomeValue = e.target.value.replace(/\D/g, '');
    const formattedIncome = incomeValue.replace(/^0+/, '');
    setMonthlyIncome(formattedIncome || null);
  };

  const handleClickCancel = () => {
    onClose();
  };
  const textAreaChangeHandler = (value: string) => {
    setTextData(value);
  };
  const validate = (): boolean => {
    const newErrors: GoalErrors = {};

    if (!goalTitle.trim()) {
      newErrors.goalTitle = 'Goal title is required';
    }

    if (!outcome?.code) {
      newErrors.outcome = 'Outcome is required';
    }

    if (outcome?.code === '2' || outcome?.code === 2) {
      if (!incomeType?.code) {
        newErrors.incomeType = 'Income type is required';
      }
      if (!monthlyIncome?.toString().trim()) {
        newErrors.monthlyIncome = 'Income is required';
      } else if (isNaN(Number(monthlyIncome))) {
        newErrors.monthlyIncome = 'Please enter a valid number';
      } else if (Number(monthlyIncome) < 0) {
        newErrors.monthlyIncome = 'Income cannot be negative';
      }
    }

    if ((outcome?.code === '1' || outcome?.code === 1) && !awardType?.value) {
      newErrors.awardType = 'Award is required';
    }

    setErrors(
      Object.fromEntries(Object.entries(newErrors).filter(([_, v]) => v !== undefined)) as {
        [key: string]: string;
      }
    );
    return Object.keys(newErrors).length === 0;
  };

  const handleAddGoal = () => {
    if (!validate()) return;

    const goalData = {
      ...(initialGoal || {}),
      aboGoalId: initialGoal?.aboGoalId,
      goalTitle,
      timeFrame: timeFrame?.code || '',
      kpiCodes: kpiCodes,
      outcome: outcome?.code || '',
      incomeType: outcomeCode === '2' ? incomeType?.code || null : null,
      monthlyIncome: outcomeCode === '2' ? monthlyIncome || null : null,
      awardType:
        outcomeCode === '1' && awardType ? { code: awardType.code, name: awardType.value } : null,
      note: textData,
    };
    onSave(goalData);
    onClose();
  };

  return (
    <div className={`add_goal_modal_cotainer ${styles.add_goal_modal_cotainer}`}>
      <div className={styles.modal_wrapper}>
        <h2 className={styles.modal_title}>{initialGoal ? t('edit_a_goal') : t('add_goal')}</h2>
        <div className={`dropdown ${styles.dropdown} ${styles.goal_title_dropdown}`}>
          <div className={styles.input_wrapper}>
            <input
              type="text"
              className={`dropdown_input ${styles.dropdown_input} ${
                goalTitle || isGoalTitleFocused ? styles.input_with_text : ''
              } ${errors?.goalTitle && 'error_outline'}`}
              value={goalTitle}
              onChange={(e) => {
                setGoalTitle(e.target.value);
                if (errors?.goalTitle) {
                  setErrors((prev) => ({ ...prev, goalTitle: '' }));
                }
              }}
              onFocus={() => setIsGoalTitleFocused(true)}
              onBlur={() => setIsGoalTitleFocused(false)}
            />
            <label
              className={`placeholder ${styles.placeholder} ${
                goalTitle || isGoalTitleFocused ? styles.label_float : ''
              }`}
            >
              {`${t('goal_title')}*`}
            </label>
          </div>
          <ErrorMessage message={errors?.goalTitle} className={styles.error_message} />
        </div>
        <div className={styles.form_group}>
          <Dropdown
            dropdownName={t('time_frame')}
            items={TIME_FRAME_TYPE}
            selectedItem={timeFrame}
            handleSelectItemCallback={setTimeFrame}
            addScroll
            className={styles.form_dropdown}
          />
        </div>
        <div className={styles.form_group}>
          <DropdownMultiselect
            dropdownName={t('performance_metrics')}
            items={PERFORMANCE_METRICS}
            selectedItems={kpiCodes}
            handleSelectItemCallback={setKpiCodes}
            addScroll
            className={styles.form_dropdown}
          />
        </div>
        <div className={styles.form_group}>
          <Dropdown
            dropdownName={`${t('outcome')}*`}
            items={OUTCOMES}
            selectedItem={outcome}
            handleSelectItemCallback={handleOutcomeChange}
            addScroll
            className={`${styles.form_dropdown}`}
            invalid={!!errors?.outcome}
            errorMsg={errors?.outcome}
          />
        </div>
        {(outcomeCode === '2' || outcomeCode === '2') && (
          <>
            <div className={styles.form_group_wrapper}>
              <div className={styles.form_group}>
                <Dropdown
                  dropdownName={`${t('income')}*`}
                  items={INCOME_TYPES}
                  selectedItem={incomeType}
                  handleSelectItemCallback={(item) => {
                    setIncomeType(item);
                    if (errors?.incomeType) {
                      setErrors((prev) => ({ ...prev, incomeType: '' }));
                    }
                  }}
                  addScroll
                  className={styles.form_dropdown}
                  invalid={!!errors?.incomeType}
                  errorMsg={errors?.incomeType}
                />
              </div>
              <div className={styles.form_group}>
                <div className={`dropdown ${styles.dropdown}`}>
                  <div className={styles.input_wrapper}>
                    <input
                      type="text"
                      className={`dropdown_input ${styles.dropdown_input}
                        ${errors?.monthlyIncome && 'error_outline'}`}
                      value={
                        monthlyIncome
                          ? CurrencyInputFormatter(currencyCode, Number(monthlyIncome))
                          : ''
                      }
                      onChange={handleIncomeChange}
                      onFocus={() => setIsIncomeFocused(true)}
                      onBlur={() => setIsIncomeFocused(false)}
                    />
                    <label
                      className={`placeholder ${styles.placeholder} ${
                        monthlyIncome || isIncomeFocused ? styles.label_float : ''
                      }`}
                    >
                      {incomeType?.code === '2' || incomeType?.code === 2 ? 'Annual' : 'Monthly'}{' '}
                      Income*
                    </label>
                  </div>
                </div>
                <ErrorMessage message={errors?.monthlyIncome} className={styles.error_message} />
              </div>
            </div>
          </>
        )}
        {(outcomeCode === '1' || outcomeCode === 1) && (
          <div className={styles.form_group}>
            <Dropdown
              dropdownName="Award*"
              items={AWARD_TYPES}
              selectedItem={awardType}
              handleSelectItemCallback={(item) => {
                setAwardType(item);
                if (errors?.awardType) {
                  setErrors((prev) => ({ ...prev, awardType: '' }));
                }
              }}
              addScroll
              className={styles.form_dropdown}
              invalid={!!errors?.awardType}
              errorMsg={errors?.awardType}
            />
          </div>
        )}

        <div className={styles.formatter_wrapper}>
          <FormatterBar
            id="addgoaldisc"
            testId="addgoaldisc"
            editorData={textData}
            handleDataChange={textAreaChangeHandler}
            section="growthplan"
            placeholder={t('add_description')}
            isContentEditable={true}
            maxCharacter={20000}
          />
        </div>
        <div className={`btn_wrapper ${styles.btn_wrapper}`}>
          <Button
            id="cancelButton"
            onClick={handleClickCancel}
            label={t('AP_info.btnCancel')}
            style="unfilled"
          />
          <Button
            id="saveButton"
            onClick={handleAddGoal}
            label={t('AP_info.btnSave')}
            style="filled"
          />
        </div>
      </div>
    </div>
  );
};

export default AddGoalModal;
